import * as Api from '@ViewModels';
import moment from 'moment';
import { CampaignType } from '../../../../models/AdminModels';
import { CalendarType } from '../../../containers/ContentCalendar/models';
import { ITemplateCard } from '../../../../viewmodels/AppViewModels';

/** Title format constants for calendar time spans. Reused by presentation.tsx. */
export const MONTH_FORMAT = 'MMMM';
export const DAY_FORMAT = 'dddd';

export interface ICalendarDropTargetComponentProps {
	droppableType: string;
	onRequestDroppableId?(moment: moment.Moment): string;
}

export interface ICampaignCalendarEventOptions {
	hideApprovalCtas?: boolean;
	showScheduledFor?: boolean;
}

export interface ICampaignCalendarTimeSpanOptions {
	eventOptions?: ICampaignCalendarEventOptions;
	groupEventsByGroupId?: boolean;
}

export interface ICampaignCalendarOptions {
	timeSpanOptions?: ICampaignCalendarTimeSpanOptions;
}

export interface ICalendarTimeSpan {
	blogReports?: Api.IBlogReportView[];
	campaigns?: Api.CampaignViewModel[];
	campaignsByGroupId?: Api.IDictionary<Api.CampaignViewModel[]>;
	moment: moment.Moment;
	socialCampaigns?: Api.SocialMediaPostReportViewModel[];
	title?: string;
	unitOfTime: moment.unitOfTime.Base;
}

export interface ICampaignCalendarEventPlaceholder<T = ITemplateCard> {
	campaignType: CampaignType;
	completedDate?: string;
	dataContext?: T;
	moment: moment.Moment;
	schedule?: Api.IScheduledSend;
	status: Api.EmailSendStatus | Api.PostStatus | Api.BlogStatus;
	subject?: string;
}

export type EventDataType =
	| Api.CampaignViewModel[]
	| Api.CampaignViewModel
	| Api.SocialMediaPostReportViewModel
	| Api.IBlogReportView;

export const SelectedPostStatuses = [
	Api.PostStatus.PartiallySucceeded,
	Api.PostStatus.Pending,
	Api.PostStatus.Scheduled,
	Api.PostStatus.Started,
	Api.PostStatus.Succeeded,
	Api.PostStatus.Cancelled,
];

export const DefaultCampaignCalendarBlogStatuses = [
	Api.BlogStatus.Pending,
	Api.BlogStatus.Queued,
	Api.BlogStatus.Completed,
];

export const getSuggestionCampaignType = (suggestion: Api.IContentCalendarSuggestion) => {
	switch (suggestion?.templateReference?.templateType) {
		case Api.TemplateType.SocialMediaPost:
			return CampaignType.Social;
		case Api.TemplateType.Blog:
			return CampaignType.Blog;
		default:
			return CampaignType.Email;
	}
};

/**
 * Build the calendar time spans for an explicit jump to `startDate`.
 *
 * Unlike the initial-render builder, this always snaps to the typed date's own
 * week/month (no "use today if same month" special case), so a jump lands exactly
 * where the user asked. Week view => 7 day spans from start-of-week; month/quarter
 * => `numberOfMonthsToShow` consecutive months from the 1st.
 *
 * `DAY_FORMAT` and `MONTH_FORMAT` are defined in this file and reused by presentation.tsx.
 */
export function buildTimeSpansFromStart(
	calendarType: CalendarType,
	numberOfMonthsToShow: number,
	startDate: moment.MomentInput
): ICalendarTimeSpan[] {
	const isWeek = calendarType === CalendarType.Weekly;
	if (isWeek) {
		const first = moment(startDate).startOf('week').startOf('day');
		const spans: ICalendarTimeSpan[] = [{ moment: first, title: first.format(DAY_FORMAT), unitOfTime: 'day' }];
		for (let i = 1; i < 7; i++) {
			const next = moment(spans[spans.length - 1].moment).add(1, 'day');
			spans.push({ moment: next, title: next.format(DAY_FORMAT), unitOfTime: 'day' });
		}
		return spans;
	}
	// Month AND Quarter share this branch; numberOfMonthsToShow drives how many months to show.
	const first = moment(startDate).startOf('month').startOf('day');
	const spans: ICalendarTimeSpan[] = [{ moment: first, title: first.format(MONTH_FORMAT), unitOfTime: 'month' }];
	const monthCount = Math.max(1, numberOfMonthsToShow);
	for (let i = 1; i < monthCount; i++) {
		const next = moment(spans[spans.length - 1].moment).add(1, 'month');
		spans.push({ moment: next, title: next.format(MONTH_FORMAT), unitOfTime: 'month' });
	}
	return spans;
}
